import React, { useState, useEffect, useRef, useCallback } from 'react';
import './App.css';
import SettingsModal from './components/SettingsModal';
import InstructionsModal from './components/InstructionsModal';
import MandalaTracker from './components/MandalaTracker';
import { loadBackgroundFromStorage, getBackgroundStyle } from './utils/backgroundManager';
import { wakeLockManager } from './utils/wakeLockManager';

const TIMER_DURATIONS = [360, 300, 180, 60, 360]; // 6min, 5min, 3min, 1min, 6min in seconds
const TIMER_LABELS = ['Sukha Kriya', 'AUM chant', 'Fluttering of Breath', 'Bandhas', 'Vipassana'];

function App() {
  const [timerState, setTimerState] = useState('idle'); // 'idle' | 'running' | 'paused' | 'completed'
  const [currentTimerIndex, setCurrentTimerIndex] = useState(0);
  const [remainingTime, setRemainingTime] = useState(TIMER_DURATIONS[0]);
  const [isTransitioning, setIsTransitioning] = useState(false);
  const [showSettings, setShowSettings] = useState(false);
  const [showInstructions, setShowInstructions] = useState(false);
  const [backgroundConfig, setBackgroundConfig] = useState(null);
  const [wakeLockActive, setWakeLockActive] = useState(false);
  
  const startTimeRef = useRef(null);
  const pausedTimeRef = useRef(null);
  const intervalRef = useRef(null);
  const audioContextRef = useRef(null);

  // Load background from localStorage on mount
  useEffect(() => {
    const savedBackground = loadBackgroundFromStorage();
    if (savedBackground) {
      setBackgroundConfig(savedBackground);
    }
  }, []);

  // Apply background dynamically
  useEffect(() => {
    const bgStyle = getBackgroundStyle(backgroundConfig);
    document.documentElement.style.setProperty('--bg-image', bgStyle);
  }, [backgroundConfig]);

  // Wake Lock: Activate when timer is running
  useEffect(() => {
    const handleWakeLock = async () => {
      if (timerState === 'running') {
        const activated = await wakeLockManager.request();
        setWakeLockActive(activated);
      } else {
        await wakeLockManager.release();
        setWakeLockActive(false);
      }
    };

    handleWakeLock();

    // Cleanup on unmount
    return () => {
      wakeLockManager.release();
      setWakeLockActive(false);
    };
  }, [timerState]);

  // Wake Lock: Reacquire on visibility change (when user returns to tab)
  useEffect(() => {
    const handleVisibilityChange = async () => {
      if (document.visibilityState === 'visible' && timerState === 'running') {
        // Reacquire wake lock when user returns to the app
        const activated = await wakeLockManager.request();
        setWakeLockActive(activated);
      }
    };

    document.addEventListener('visibilitychange', handleVisibilityChange);
    
    return () => {
      document.removeEventListener('visibilitychange', handleVisibilityChange);
    };
  }, [timerState]);

  // Initialize Audio Context
  useEffect(() => {
    audioContextRef.current = new (window.AudioContext || window.webkitAudioContext)();
    return () => {
      if (audioContextRef.current) {
        audioContextRef.current.close();
      }
    };
  }, []);

  // Play beep sound
  const playBeep = useCallback(() => {
    if (!audioContextRef.current) return;
    
    const ctx = audioContextRef.current;
    const oscillator = ctx.createOscillator();
    const gainNode = ctx.createGain();
    
    oscillator.connect(gainNode);
    gainNode.connect(ctx.destination);
    
    oscillator.frequency.value = 800; // 800Hz beep
    oscillator.type = 'sine';
    
    gainNode.gain.setValueAtTime(0.3, ctx.currentTime);
    gainNode.gain.exponentialRampToValueAtTime(0.01, ctx.currentTime + 0.2);
    
    oscillator.start(ctx.currentTime);
    oscillator.stop(ctx.currentTime + 0.2);
  }, []);

  // Visual transition effect
  const triggerTransition = useCallback(() => {
    setIsTransitioning(true);
    setTimeout(() => setIsTransitioning(false), 500);
  }, []);

  // Move to next timer
  const moveToNextTimer = useCallback(() => {
    if (currentTimerIndex < TIMER_DURATIONS.length - 1) {
      const nextIndex = currentTimerIndex + 1;
      setCurrentTimerIndex(nextIndex);
      setRemainingTime(TIMER_DURATIONS[nextIndex]);
      startTimeRef.current = Date.now();
      pausedTimeRef.current = TIMER_DURATIONS[nextIndex];
      playBeep();
      triggerTransition();
    } else {
      // Sequence completed
      setTimerState('completed');
      playBeep();
      triggerTransition();
      if (intervalRef.current) {
        clearInterval(intervalRef.current);
      }
    }
  }, [currentTimerIndex, playBeep, triggerTransition]);

  // Timer countdown logic
  useEffect(() => {
    if (timerState === 'running') {
      intervalRef.current = setInterval(() => {
        const elapsed = Math.floor((Date.now() - startTimeRef.current) / 1000);
        const timeLeft = pausedTimeRef.current - elapsed;
        
        if (timeLeft <= 0) {
          moveToNextTimer();
        } else {
          setRemainingTime(timeLeft);
        }
      }, 100); // Update every 100ms for smooth display

      return () => {
        if (intervalRef.current) {
          clearInterval(intervalRef.current);
        }
      };
    }
  }, [timerState, moveToNextTimer]);

  // Format time as MM:SS
  const formatTime = (seconds) => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins.toString().padStart(2, '0')}:${secs.toString().padStart(2, '0')}`;
  };

  // Calculate total time from TIMER_DURATIONS array
  const getTotalTime = () => {
    const totalSeconds = TIMER_DURATIONS.reduce((sum, duration) => sum + duration, 0);
    return formatTime(totalSeconds);
  };

  // Start the sequence
  const handleStart = useCallback(() => {
    setTimerState('running');
    startTimeRef.current = Date.now();
    pausedTimeRef.current = remainingTime;
    playBeep();
    triggerTransition();
  }, [remainingTime, playBeep, triggerTransition]);

  // Pause the timer
  const handlePause = useCallback(() => {
    setTimerState('paused');
    if (intervalRef.current) {
      clearInterval(intervalRef.current);
    }
  }, []);

  // Resume the timer
  const handleResume = useCallback(() => {
    setTimerState('running');
    startTimeRef.current = Date.now();
    pausedTimeRef.current = remainingTime;
  }, [remainingTime]);

  // Reset to beginning
  const handleReset = useCallback(() => {
    setTimerState('idle');
    setCurrentTimerIndex(0);
    setRemainingTime(TIMER_DURATIONS[0]);
    startTimeRef.current = null;
    pausedTimeRef.current = null;
    if (intervalRef.current) {
      clearInterval(intervalRef.current);
    }
  }, []);

  // Skip to next timer
  const handleNext = useCallback(() => {
    if (currentTimerIndex < TIMER_DURATIONS.length - 1) {
      const nextIndex = currentTimerIndex + 1;
      setCurrentTimerIndex(nextIndex);
      setRemainingTime(TIMER_DURATIONS[nextIndex]);
      startTimeRef.current = Date.now();
      pausedTimeRef.current = TIMER_DURATIONS[nextIndex];
      playBeep();
      triggerTransition();
    } else {
      // On last timer, complete the sequence
      setTimerState('completed');
      playBeep();
      triggerTransition();
      if (intervalRef.current) {
        clearInterval(intervalRef.current);
      }
    }
  }, [currentTimerIndex, playBeep, triggerTransition]);

  // Keyboard shortcuts
  useEffect(() => {
    const handleKeyPress = (e) => {
      if (e.code === 'Space') {
        e.preventDefault();
        if (timerState === 'idle') handleStart();
        else if (timerState === 'running') handlePause();
        else if (timerState === 'paused') handleResume();
      } else if (e.code === 'KeyR') {
        e.preventDefault();
        handleReset();
      } else if (e.code === 'KeyN' && timerState === 'running') {
        e.preventDefault();
        handleNext();
      }
    };

    window.addEventListener('keydown', handleKeyPress);
    return () => window.removeEventListener('keydown', handleKeyPress);
  }, [timerState, remainingTime, handleStart, handlePause, handleResume, handleReset, handleNext]);

  return (
    <div className="app-container">
      {/* Wake Lock Indicator - Disabled (functionality still active) */}
      {/* {wakeLockActive && (
        <div className="wake-lock-indicator" title="Screen will stay awake">
          <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor">
            <path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm0 18c-4.41 0-8-3.59-8-8s3.59-8 8-8 8 3.59 8 8-3.59 8-8 8zm-1-13h2v6h-2zm0 8h2v2h-2z"/>
          </svg>
          <span>Screen Awake</span>
        </div>
      )} */}

      {/* Info Button */}
      <button 
        className="info-button"
        onClick={() => setShowInstructions(true)}
        aria-label="View instructions"
        title="Instructions"
        data-testid="info-button"
      >
        <img 
          src="https://customer-assets.emergentagent.com/job_yoga-sadhana-1/artifacts/hlj6mbto_lotus.png" 
          alt="Instructions"
          width="28"
          height="28"
        />
      </button>

      {/* Settings Button */}
      <button 
        className="settings-button"
        onClick={() => setShowSettings(true)}
        aria-label="Open settings"
        title="Background Settings"
      >
        <img 
          src="https://customer-assets.emergentagent.com/job_yoga-sadhana-1/artifacts/cosqucbr_setting.png" 
          alt="Settings"
          width="28"
          height="28"
        />
      </button>

      {/* Settings Modal */}
      <SettingsModal
        isOpen={showSettings}
        onClose={() => setShowSettings(false)}
        onBackgroundChange={setBackgroundConfig}
        currentBackground={backgroundConfig}
      />

      {/* Instructions Modal */}
      <InstructionsModal
        isOpen={showInstructions}
        onClose={() => setShowInstructions(false)}
      />

      <div className="main-content">
        <div className={`timer-card ${timerState === 'completed' ? 'completed-state' : ''}`}>
          <h1 className="app-title" data-testid="app-title">Isha Yoga Sadhana</h1>
        
        {timerState !== 'completed' ? (
          <>
            <div 
              className={`timer-display ${isTransitioning ? 'transitioning' : ''} ${timerState === 'paused' ? 'paused' : ''}`}
              data-testid="timer-display"
            >
              <div className="timer-label" data-testid="timer-label">
                {TIMER_LABELS[currentTimerIndex]}
              </div>
              <div className="timer-countdown" data-testid="timer-countdown">
                {formatTime(remainingTime)}
              </div>
              {timerState === 'paused' && (
                <div className="paused-indicator" data-testid="paused-indicator">(PAUSED)</div>
              )}
            </div>

            <div className="progress-indicators" data-testid="progress-indicators">
              {TIMER_LABELS.map((_, index) => (
                <div
                  key={index}
                  className={`progress-dot ${
                    index < currentTimerIndex ? 'completed' : 
                    index === currentTimerIndex ? 'active' : 
                    'pending'
                  }`}
                  data-testid={`progress-dot-${index}`}
                />
              ))}
            </div>

            <div className="controls" data-testid="controls">
              {timerState === 'idle' && (
                <button 
                  className="btn btn-primary btn-large btn-icon"
                  onClick={handleStart}
                  data-testid="start-button"
                  aria-label="Start timer sequence"
                  title="Start"
                >
                  <svg width="32" height="32" viewBox="0 0 24 24" fill="currentColor">
                    <path d="M8 5v14l11-7z"/>
                  </svg>
                </button>
              )}
              
              {timerState === 'running' && (
                <>
                  <button 
                    className="btn btn-secondary btn-icon"
                    onClick={handlePause}
                    data-testid="pause-button"
                    aria-label="Pause timer"
                    title="Pause"
                  >
                    <svg width="28" height="28" viewBox="0 0 24 24" fill="currentColor">
                      <path d="M6 4h4v16H6V4zm8 0h4v16h-4V4z"/>
                    </svg>
                  </button>
                  <button 
                    className="btn btn-info btn-icon"
                    onClick={handleNext}
                    data-testid="next-button"
                    aria-label="Skip to next timer"
                    title="Next"
                  >
                    <svg width="28" height="28" viewBox="0 0 24 24" fill="currentColor">
                      <path d="M6 4l12 8-12 8V4zm13 0v16h2V4h-2z"/>
                    </svg>
                  </button>
                  <button 
                    className="btn btn-danger btn-icon"
                    onClick={handleReset}
                    data-testid="reset-button"
                    aria-label="Reset timer sequence"
                    title="Reset"
                  >
                    <svg width="28" height="28" viewBox="0 0 24 24" fill="currentColor">
                      <path d="M17.65 6.35C16.2 4.9 14.21 4 12 4c-4.42 0-7.99 3.58-7.99 8s3.57 8 7.99 8c3.73 0 6.84-2.55 7.73-6h-2.08c-.82 2.33-3.04 4-5.65 4-3.31 0-6-2.69-6-6s2.69-6 6-6c1.66 0 3.14.69 4.22 1.78L13 11h7V4l-2.35 2.35z"/>
                    </svg>
                  </button>
                </>
              )}
              
              {timerState === 'paused' && (
                <>
                  <button 
                    className="btn btn-primary btn-icon"
                    onClick={handleResume}
                    data-testid="resume-button"
                    aria-label="Resume timer"
                    title="Resume"
                  >
                    <svg width="28" height="28" viewBox="0 0 24 24" fill="currentColor">
                      <path d="M8 5v14l11-7z"/>
                    </svg>
                  </button>
                  <button 
                    className="btn btn-danger btn-icon"
                    onClick={handleReset}
                    data-testid="reset-button"
                    aria-label="Reset timer sequence"
                    title="Reset"
                  >
                    <svg width="28" height="28" viewBox="0 0 24 24" fill="currentColor">
                      <path d="M17.65 6.35C16.2 4.9 14.21 4 12 4c-4.42 0-7.99 3.58-7.99 8s3.57 8 7.99 8c3.73 0 6.84-2.55 7.73-6h-2.08c-.82 2.33-3.04 4-5.65 4-3.31 0-6-2.69-6-6s2.69-6 6-6c1.66 0 3.14.69 4.22 1.78L13 11h7V4l-2.35 2.35z"/>
                    </svg>
                  </button>
                </>
              )}
            </div>

          </>
        ) : (
          <div className="completion-screen" data-testid="completion-screen">
            <div className="completion-icon">✓</div>
            <h2 className="completion-title">Congrats on completing your daily Sadhana!</h2>
            <div className="completion-time" data-testid="completion-time">
              Total Time: {getTotalTime()}
            </div>
            <button 
              className="btn btn-primary btn-large"
              onClick={handleReset}
              data-testid="start-over-button"
              aria-label="Start sequence over"
            >
              Start Over
            </button>
          </div>
        )}
        </div>

        {/* Mandala Habit Tracker */}
        <MandalaTracker />
      </div>
    </div>
  );
}

export default App;
